import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { Box, Typography, Stack, Popover, Paper, Grid, SxProps, Theme, useTheme } from '@mui/material';
import {
    EquipmentOrderData,
    SunburstNode,
    SunburstChartProps,
    SunburstTooltipData,
    HIERARCHY_LEVELS,
    DEFAULT_COLOR_SCHEME
} from './types/sunburst.types';
import { FilterData } from './PlantsFilter';

interface DetailedTooltipData {
    title: string;
    subtitle: string;
    globalSubtitle: string;
    details: string[];
}

type D3HierarchyNode = d3.HierarchyRectangularNode<SunburstNode>;

/**
 * EquipmentOrdersSunburst Component
 * 
 * A D3.js-powered sunburst chart that visualizes equipment orders in a hierarchical format:
 * Center -> MACHINETYPE -> MACHINEPRODUCT -> MACHINEMARKET -> MACHINEVARIANT -> MACHINESIZE
 */
export const EquipmentOrdersSunburst: React.FC<SunburstChartProps & {
    data: EquipmentOrderData[];
    filteredData?: EquipmentOrderData[];
    currentYear?: number;
    filters?: FilterData;
    sx?: SxProps<Theme>;
}> = ({
    data,
    filteredData,
    currentYear = 2025,
    filters,
    width = 800,
    height = 400,
    className,
    onNodeClick,
    onNodeHover,
    colorScheme = DEFAULT_COLOR_SCHEME,
    sx
}) => {
        const svgRef = useRef<SVGSVGElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);
        const theme = useTheme();
        
        // Popover state for MUI tooltip
        const [popoverOpen, setPopoverOpen] = useState(false);
        const [selectedNodeData, setSelectedNodeData] = useState<SunburstTooltipData | null>(null);

        // Use maximum available radius with padding and minimum inner radius
        const maxRadius = Math.min(width, height) / 2 - 20;
        const minInnerRadius = maxRadius * 0.15; // Minimum 15% of radius for center
        const actualData = filteredData ?? data;

        // Transform flat data into hierarchical structure
        const hierarchyData = useMemo((): D3HierarchyNode | null => {
            if (!actualData?.length) return null;

            // Build nested structure with custom hierarchy: PRODUCT -> MARKET -> VARIANT (split) -> INDIVIDUAL
            const nested: any = { name: 'root', children: [] };

            actualData.forEach(item => {
                // Level 1: MACHINEPRODUCT
                const product = item.MACHINEPRODUCT || 'Unknown';
                let productNode = nested.children.find((c: any) => c.name === product);
                if (!productNode) {
                    productNode = {
                        name: product,
                        children: [],
                        value: 0,
                        depth: 1,
                        data: []
                    };
                    nested.children.push(productNode);
                }

                // Level 2: MACHINEMARKET
                const market = item.MACHINEMARKET || 'Unknown';
                let marketNode = productNode.children.find((c: any) => c.name === market);
                if (!marketNode) {
                    marketNode = {
                        name: market,
                        children: [],
                        value: 0,
                        depth: 2,
                        data: []
                    };
                    productNode.children.push(marketNode);
                }

                // Level 3: MACHINEVARIANT (split by '-' or ' ')
                const variant = item.MACHINEVARIANT || 'Unknown';
                // Split variant by '-' or ' ' and take the first meaningful part
                const variantParts = variant.split(/[-\s]+/).filter(part => part.length > 0);
                const variantKey = variantParts.length > 0 ? variantParts[0] : variant;

                let variantNode = marketNode.children.find((c: any) => c.name === variantKey);
                if (!variantNode) {
                    variantNode = {
                        name: variantKey,
                        children: [],
                        value: 0,
                        depth: 3,
                        data: []
                    };
                    marketNode.children.push(variantNode);
                }

                // Level 4: Dynamic based on filter state
                let individual: string;
                
                // Determine what to show in 4th level based on current filter state
                const hasRegion = filters?.region && filters.region.trim() !== '';
                const hasCountry = filters?.country && filters.country.trim() !== '';
                
                // Priority logic: Country selection immediately shows plants
                if (hasCountry) {
                    // Country selected -> show plants (companies + plant IDs)
                    individual = `${item.Company_Name || 'Unknown'} - Plant ${item.Plant_ID || 'N/A'}`;
                } else if (hasRegion) {
                    // Region selected but no country -> show countries  
                    individual = item.Country || 'Unknown';
                } else {
                    // No filters -> show regions
                    individual = item.Region || 'Unknown';
                }
                
                let individualNode = variantNode.children.find((c: any) => c.name === individual);
                if (!individualNode) {
                    individualNode = {
                        name: individual,
                        value: 0,
                        depth: 4,
                        data: []
                    };
                    variantNode.children.push(individualNode);
                }

                // Add data at all levels for reference, but only set value at leaf level
                individualNode.data.push(item);
                individualNode.value = Number(individualNode.value || 0) + 1; // Only count at leaf level

                variantNode.data.push(item);
                // Don't set value here - let D3 sum from children

                marketNode.data.push(item);
                // Don't set value here - let D3 sum from children

                productNode.data.push(item);
                // Don't set value here - let D3 sum from children
            });

            // Create D3 hierarchy - sum from leaf nodes where we set values
            const hierarchy = d3.hierarchy(nested)
                .sum((d: any) => {
                    // Sum from nodes that have a value set (leaf nodes)
                    return d.value || 0;
                })
                .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

            // Apply partition layout with optimized inner radius
            const usableRadius = maxRadius - minInnerRadius;
            const partition = d3.partition<any>()
                .size([2 * Math.PI, usableRadius])
                .padding(0.002); // Small padding between segments for visual separation

            return partition(hierarchy);
        }, [actualData, maxRadius, minInnerRadius, filters]);

        // Hierarchical color scales for better contrast
        const getColorForNode = useMemo(() => {
            return (node: D3HierarchyNode): string => {
                const depth = node.depth;
                const segmentName = node.data.name;
                
                // Create a hash of the segment name for consistent coloring
                let hash = 0;
                for (let i = 0; i < segmentName.length; i++) {
                    hash = ((hash << 5) - hash + segmentName.charCodeAt(i)) & 0xffffffff;
                }
                
                // Use different color palettes based on hierarchy level for better contrast
                let levelColors: string[];
                
                switch (depth) {
                    case 1: // MACHINEPRODUCT - Vibrant colors
                        levelColors = [
                            '#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd',
                            '#8c564b', '#e377c2', '#17becf', '#bcbd22', '#e8c547'
                        ];
                        break;
                    case 2: // MACHINEMARKET - Medium saturation
                        levelColors = [
                            '#4c94c4', '#ff9f3e', '#4cb04c', '#e04748', '#b487d5',
                            '#ac766b', '#f797e2', '#37cedf', '#dce542', '#f8d567'
                        ];
                        break;
                    case 3: // MACHINEVARIANT - Lighter colors
                        levelColors = [
                            '#7fb1d4', '#ffbf6e', '#6cc06c', '#e86768', '#c4a7e5',
                            '#cc968b', '#fbb7f2', '#57deef', '#ece562', '#fde587'
                        ];
                        break;
                    default: // Individual items - Very light colors
                        levelColors = [
                            '#aec7e8', '#ffdf9e', '#98df8a', '#ff9896', '#d4c7f5',
                            '#dbb6ab', '#fdd7ff', '#87eeff', '#fcf582', '#ffe5a7'
                        ];
                        break;
                }
                
                // Get consistent color index based on segment name hash
                const colorIndex = Math.abs(hash) % levelColors.length;
                return levelColors[colorIndex];
            };
        }, []);

        // Custom click-outside handler for MUI popover
        useEffect(() => {
            const handleClickOutside = (event: MouseEvent): void => {
                if (popoverOpen && containerRef.current && !containerRef.current.contains(event.target as Node)) {
                    // Check if click is not on the popover content itself
                    const popoverElement = document.querySelector('[role="presentation"] .MuiPopover-paper');
                    if (popoverElement && !popoverElement.contains(event.target as Node)) {
                        setPopoverOpen(false);
                        setSelectedNodeData(null);
                    }
                }
            };

            if (popoverOpen) {
                document.addEventListener('mousedown', handleClickOutside);
            }

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [popoverOpen]);

        // Draw the sunburst chart
        useEffect(() => {
            if (!svgRef.current) return;

            const svg = d3.select(svgRef.current);
            svg.selectAll("*").remove();

            // If no data, show empty state and return early
            if (!hierarchyData) {
                svg.append("text")
                    .attr("x", width / 2)
                    .attr("y", height / 2)
                    .attr("text-anchor", "middle")
                    .attr("dy", "0.35em")
                    .style("font-size", "16px")
                    .style("fill", theme.palette.text.secondary)
                    .text("No data available for selected filters");
                return;
            }

            const g = svg
                .append("g")
                .attr("transform", `translate(${width / 2}, ${height / 2})`);

            // Arc generator - map partition coordinates to actual radius with minimum inner radius
            const arc = d3.arc<D3HierarchyNode>()
                .startAngle(d => d.x0)
                .endAngle(d => d.x1)
                .innerRadius(d => minInnerRadius + (d.y0 ?? 0))
                .outerRadius(d => minInnerRadius + (d.y1 ?? 0));

            // Get nodes (exclude root)
            const nodes = hierarchyData.descendants().slice(1);

            // Create arcs
            const paths = g.selectAll("path")
                .data(nodes)
                .enter()
                .append("path")
                .attr("d", arc)
                .style("fill", (d: D3HierarchyNode) => {
                    return getColorForNode(d);
                })
                .style("stroke", (d: D3HierarchyNode) => {
                    // Use a darker version of the fill color for stroke
                    const fillColor = getColorForNode(d);
                    return d3.color(fillColor)?.darker(0.3)?.toString() ?? fillColor;
                })
                .style("stroke-width", (d: D3HierarchyNode) => {
                    // Vary stroke width by depth for better hierarchy definition
                    return d.depth === 1 ? 2 : d.depth === 2 ? 1.5 : 1;
                })
                .style("cursor", "pointer")
                .style("opacity", (d: D3HierarchyNode) => {
                    // Higher opacity for outer levels to enhance contrast
                    return d.depth === 1 ? 0.95 : d.depth === 2 ? 0.85 : 0.75;
                });

            // Add click handler for detailed popover
            paths
                .on("click", function (event: MouseEvent, d: D3HierarchyNode) {
                    event.stopPropagation();

                    // Create detailed tooltip data for popover
                    const path = d.ancestors().reverse().map((node: D3HierarchyNode) => node.data.name);
                    const detailedTooltipData: SunburstTooltipData = {
                        node: d.data,
                        percentage: ((d.value ?? 0) / (hierarchyData.value ?? 1)) * 100,
                        path: path.slice(1), // Remove root
                        orderCount: d.value ?? 0,
                        level: HIERARCHY_LEVELS[d.depth - 1]?.label || 'Unknown'
                    };

                    setSelectedNodeData(detailedTooltipData);
                    setPopoverOpen(true);

                    if (onNodeClick) {
                        onNodeClick(d.data);
                    }
                });

            // Add text labels for larger segments
            g.selectAll("text")
                .data(nodes.filter((d: D3HierarchyNode) => d.x1 - d.x0 > 0.1))
                .enter()
                .append("text")
                .attr("transform", (d: D3HierarchyNode) => {
                    const angle = (d.x0 + d.x1) / 2 * 180 / Math.PI;
                    const radius = minInnerRadius + (d.y0 + d.y1) / 2;
                    return `rotate(${angle - 90}) translate(${radius},0) rotate(${angle > 90 ? 180 : 0})`;
                })
                .attr("dy", "0.35em")
                .style("text-anchor", (d: D3HierarchyNode) => {
                    const angle = (d.x0 + d.x1) / 2;
                    return angle > Math.PI ? "end" : "start";
                })
                .style("font-size", "10px")
                .style("fill", theme.palette.text.primary)
                .style("pointer-events", "none")
                .text((d: D3HierarchyNode) => {
                    const name = d.data.name;
                    const maxLength = Math.max(5, (d.x1 - d.x0) * 20);
                    return name.length > maxLength ? name.substring(0, maxLength) + "..." : name;
                });

        }, [hierarchyData, maxRadius, minInnerRadius, width, height, getColorForNode, onNodeClick, onNodeHover, theme]);

        // Function to create detailed tooltip information
        const getDetailedTooltipData = (tooltipData: SunburstTooltipData): DetailedTooltipData => {
            if (!tooltipData) return { title: '', subtitle: '', globalSubtitle: '', details: [] };

            const title = tooltipData.node.name;
            const subtitle = `${tooltipData.level} • ${tooltipData.orderCount.toLocaleString()} orders (${tooltipData.percentage.toFixed(1)}%)`;
            
            // Global subtitle showing overall market context
            const globalSubtitle = `🌐 Global Equipment Market: ${tooltipData.percentage.toFixed(2)}% of total orders (${tooltipData.orderCount.toLocaleString()} orders globally)`;

            // Section 1: Basic info
            const section1: string[] = [
                `- Segment: ${tooltipData.node.name}`,
                `- Hierarchy Level: ${tooltipData.level}`,
                `- Order Volume: ${tooltipData.orderCount.toLocaleString()} orders`,
                `- Market Share: ${tooltipData.percentage.toFixed(2)}% of total orders`
            ];

            // Section 2: Hierarchy path
            const section2: string[] = [
                `- Hierarchy Path:`,
                ...tooltipData.path.map((pathItem, index) => 
                    `   ${'└'.repeat(Math.min(index, 3))} ${pathItem}`
                )
            ];

            // Section 3: Data summary (goes to third column)
            const section3: string[] = [];
            
            // Add data insights if available
            if (tooltipData.node.data && tooltipData.node.data.length > 0) {
                const companies = new Set(tooltipData.node.data.map(d => d.Company_Name)).size;
                const countries = new Set(tooltipData.node.data.map(d => d.COUNTRYCODE)).size;
                const machines = new Set(tooltipData.node.data.map(d => d.MACHINETYPE)).size;

                section3.push(
                    `- Data Summary:`,
                    `    • Companies: ${companies}`,
                    `    • Countries: ${countries}`,
                    `    • Machine Types: ${machines}`,
                    `    • Total Records: ${tooltipData.node.data.length}`
                );

                // Show sample companies for small segments
                if (companies <= 5 && tooltipData.node.data.length <= 10) {
                    const uniqueCompanies = Array.from(new Set(tooltipData.node.data.map(d => d.Company_Name)));
                    section3.push(``, `🏭 Companies in this segment:`);
                    uniqueCompanies.forEach((company, index) => {
                        section3.push(`    ${index + 1}. ${company}`);
                    });
                }
            }

            // Combine sections with appropriate spacing
            const details = [
                ...section1,
                ``,
                ...section2,
                ``,
                ...section3
            ];

            return { title, subtitle, globalSubtitle, details };
        };

        const totalOrders = actualData?.length || 0; // Use actual record count, not hierarchy value

        return (
            <Stack ref={containerRef} direction="column" sx={{ width:'100%', position: 'relative', ...sx }} >
                {/* Header */}
                <Box sx={{ 
                    mb: 1, 
                    left:'2em',
                    backgroundColor: theme.palette.background.paper,
                    p: 1,
                    borderRadius: 1
                }}>
                    <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                        <strong>Current View:</strong> {currentYear} • Showing {totalOrders.toLocaleString()} records
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5, color: theme.palette.text.secondary }}>
                        Data Source: Equipment Orders API - {totalOrders.toLocaleString()} total records loaded
                    </Typography>
                </Box>

                {/* SVG Chart */}
                <Box sx={{
                    flexGrow: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: height,
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: 1
                }}>
                    <svg
                        ref={svgRef}
                        width={width}
                        height={height}
                        style={{ maxWidth: '100%', height: 'auto' }}
                    />
                </Box>

                {/* MUI Popover for detailed segment analysis */}
                <Popover
                    open={popoverOpen}
                    anchorEl={containerRef.current}
                    onClose={() => {
                        setPopoverOpen(false);
                        setSelectedNodeData(null);
                    }}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    disableRestoreFocus
                    sx={{
                        '& .MuiPopover-paper': {
                            position: 'fixed',
                            top: '60px !important',
                            left: '50% !important',
                            transform: 'translateX(-50%) !important',
                            maxWidth: 900,
                            minWidth: 750,
                            backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(30, 30, 30, 0.98)' 
                                : 'rgba(255, 255, 255, 0.98)',
                            color: theme.palette.text.primary,
                            borderRadius: 3,
                            boxShadow: theme.palette.mode === 'dark'
                                ? '0 12px 48px rgba(0, 0, 0, 0.8)'
                                : '0 12px 48px rgba(0, 0, 0, 0.3)',
                            border: `2px solid ${theme.palette.divider}`,
                            zIndex: 9999,
                            backdropFilter: 'blur(8px)',
                        },
                        '& .MuiBackdrop-root': {
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                        }
                    }}
                >
                    {selectedNodeData && (
                        <Paper sx={{ 
                            p: 2, 
                            backgroundColor: 'transparent',
                            color: theme.palette.text.primary,
                            boxShadow: 'none'
                        }}>
                            {(() => {
                                const tooltipData = getDetailedTooltipData(selectedNodeData);
                                const detailsPerColumn = Math.ceil(tooltipData.details.length / 3);
                                const column1 = tooltipData.details.slice(0, detailsPerColumn);
                                const column2 = tooltipData.details.slice(detailsPerColumn, detailsPerColumn * 2);
                                const column3 = tooltipData.details.slice(detailsPerColumn * 2);
                                
                                return (
                                    <Grid container spacing={2}>
                                        {/* First Column - Title + First Section */}
                                        <Grid size={4}>
                                            <Typography variant="h6" sx={{ fontSize: '13px', fontWeight: 'bold', mb: 0.5 }}>
                                                {tooltipData.title}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '12px', color: theme.palette.text.secondary, mb: 0.5 }}>
                                                {tooltipData.subtitle}
                                            </Typography>
                                            {column1.map((detail, index) => (
                                                <Typography key={index} variant="body2" sx={{ fontSize: '11px', mb: 0.3 }}>
                                                    {detail}
                                                </Typography>
                                            ))}
                                        </Grid>
                                        
                                        {/* Second Column */}
                                        <Grid size={4} sx={{ 
                                            borderLeft: `1px solid ${theme.palette.divider}`, 
                                            paddingLeft: 2 
                                        }}>
                                            {column2.map((detail, index) => (
                                                <Typography key={index} variant="body2" sx={{ fontSize: '11px', mb: 0.3 }}>
                                                    {detail}
                                                </Typography>
                                            ))}
                                        </Grid>
                                        
                                        {/* Third Column */}
                                        <Grid size={4} sx={{ 
                                            borderLeft: `1px solid ${theme.palette.divider}`, 
                                            paddingLeft: 2 
                                        }}>
                                            {column3.map((detail, index) => (
                                                <Typography key={index} variant="body2" sx={{ fontSize: '11px', mb: 0.3 }}>
                                                    {detail}
                                                </Typography>
                                            ))}
                                        </Grid>
                                    </Grid>
                                );
                            })()}
                        </Paper>
                    )}
                </Popover>

            </Stack>
        );
    };

export default EquipmentOrdersSunburst;